import React, { useCallback, useState } from "react";

import CopyLinkButton from "../CopyLinkButton";
import { Constants } from "../../constants/Lookups";
import { VideoProvider } from "../../constants/Appointment";
import { getZoomHostStartUrl } from "../../api-helpers/AppointmentApiHelper";
import { formatPhoneNumber, hasPhoneNumber, toTelHref } from "../../helper/phoneUtils";
import type { AppointmentConnectivity } from "../../store/slices/appointmentOverlaySlice";

type AppointmentOverlayConnectivityProps = {
  connectivity: AppointmentConnectivity;
  appointmentId: string | null;
  onError: (message: string) => void;
};

/**
 * How the CLINICIAN joins this visit -- a single control that sits in the card header,
 * beside the task count.
 *
 * Only the clinician's own way in appears here -- the patient-facing `webinarUrl` is
 * not shown or copyable, since handing a patient their link is a job for the
 * appointment page, not the widget a clinician works out of.
 *
 * A video visit gets a Join button: Zoom resolves a fresh host URL at click time and
 * opens in a new tab so the overlay survives the call. Legacy non-Zoom video visits
 * can't be joined until rescheduled, so their button is disabled. A phone visit gets a
 * Call button carrying the number, with the number itself on the label for screen
 * readers and on hover.
 */
const AppointmentOverlayConnectivity = ({
  connectivity,
  appointmentId,
  onError,
}: AppointmentOverlayConnectivityProps) => {
  const isVideo = connectivity.mode === Constants.VIDEO_MODE;
  const isZoom = connectivity.videoProvider === VideoProvider.ZOOM;
  const [joining, setJoining] = useState(false);

  const phoneHref = toTelHref(connectivity.phone);
  const canCall = hasPhoneNumber(connectivity.phone) && phoneHref !== "";

  const handleJoin = useCallback(async () => {
    if (!isZoom || !appointmentId) return;
    setJoining(true);
    try {
      const startUrl = await getZoomHostStartUrl(appointmentId);
      window.open(startUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to open the Zoom host link:", error);
      onError("Could not open the video call. Please try again.");
    } finally {
      setJoining(false);
    }
  }, [isZoom, appointmentId, onError]);

  const canJoin = isVideo && isZoom && !!appointmentId;

  const dialLabel = canCall
    ? `Call ${formatPhoneNumber(connectivity.phone)}`
    : "No number on file";

  // Call and copy act on the same number, so they read as one segmented control.
  const joined = !isVideo && canCall;

  return (
    <div
      className={`appointment-overlay__connectivity${
        joined ? " appointment-overlay__connectivity--joined" : ""
      }`}
    >
      {isVideo ? (
        <button
          type="button"
          className={`appointment-overlay__join-btn${
            isZoom ? " appointment-overlay__join-btn--zoom" : ""
          }`}
          onClick={handleJoin}
          disabled={!canJoin || joining}
          title={connectivity.mode}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            videocam
          </span>
          {joining ? "Opening..." : isZoom ? "Join Zoom" : "Join"}
        </button>
      ) : (
        // Call and copy share one Dialpad-gradient pill when there's a number.
        <span className={canCall ? "appointment-overlay__dial" : "appointment-overlay__dial-off"}>
          <a
            className={`appointment-overlay__join-btn${
              canCall ? "" : " appointment-overlay__join-btn--disabled"
            }`}
            href={canCall ? `tel:${phoneHref}` : undefined}
            aria-disabled={!canCall}
            aria-label={dialLabel}
            title={dialLabel}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              call
            </span>
            {/* The number is the label: it is both the target and the thing a
                clinician may need to read off, and it costs no extra space. */}
            {canCall ? formatPhoneNumber(connectivity.phone) : "No number"}
          </a>
          {canCall && (
            <CopyLinkButton
              value={formatPhoneNumber(connectivity.phone)}
              title="Copy number"
              successMessage="Number copied to clipboard"
              errorMessage="Could not copy the number"
            />
          )}
        </span>
      )}
    </div>
  );
};

export default React.memo(AppointmentOverlayConnectivity);
